<template>
  <a-modal
    :open="open"
    class="sequence-contacts"
    @cancel="handleClose"
    centered
    :maskClosable="false"
    :title="`Contactos asignados a la secuencia “${sequenceName}”`"
    :width="850"
    :bodyStyle="{ padding: '20px 0' }"
  >
    <span class="description" style="margin-bottom: 20px">
      Esta es la lista de contacto actuales en la secuencia. Puedes optar por quitar a un contacto
      de esta secuencia.
    </span>
    <div class="contacts-list">
      <div class="refresh-wrapper">
        <a-button style="padding: 0" type="link" @click="fetchAgentsAllPages()">
          Actualizar datos recientes
        </a-button>
      </div>
      <div v-if="selectedRowKeys.length > 0">
        <div class="counter">
          <a-button type="link" @click.prevent="handleDelete()">
            Eliminar contacto(s) de la secuencia <DeleteOutlined />
          </a-button>
        </div>
      </div>
      <a-table
        :columns="columns"
        :data-source="contactsData"
        :loading="loading"
        :row-selection="rowSelection"
        :row-key="(record: any) => record.id"
        size="small"
        :pagination="false"
        :scroll="{ x: 700, y: 430 }"
      >
        <template #bodyCell="{ column, record, text, index }">
          <template v-if="column.key === 'state'">
            <a-tag :color="ContactStatusTag[record.state]?.color">
              {{ ContactStatusTag[record.state]?.title }}
            </a-tag>
          </template>
          <template v-if="column.key === 'created_at'">
            {{ dayjs(record.created_at).format("DD-MM-YYYY HH:mm:ss") }}
          </template>
          <template v-if="column.key === 'action'">
            <a-button
              type="link"
              :disabled="
                !(record.state == ContactStatus.Pending || record.state == ContactStatus.InProgress)
              "
              class="action-button"
              @click="handleDelete(record)"
            >
              <DeleteOutlined />
            </a-button>
            <div v-if="index === contactsData.length - 1" ref="observerFlagRef" />
          </template>
        </template>
      </a-table>
    </div>

    <template #footer>
      <a-button class="footer_btn" @click="handleClose">Cerrar</a-button>
    </template>
  </a-modal>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, createVNode } from "vue";
import { sequenceService } from "@/app/flows/sequences/services/Sequence.service";
import { contactsTableColumns } from "@/app/flows/sequences/utils/columns";
import { ContactStatus } from "@/app/flows/sequences/utils/enums";
import { ContactStatusTag } from "@/app/flows/sequences/utils/texts";
import { DeleteOutlined, QuestionCircleOutlined } from "@ant-design/icons-vue";
import type { TableProps } from "ant-design-vue";
import { SequenceAgent } from "@/@types/flows/sequences";
import { Modal, message } from "ant-design-vue";
import dayjs from "dayjs";
import { useIntersectionObserver } from "@vueuse/core";

const props = defineProps<{ sequenceId: number; sequenceName: string; open: boolean }>();

const emit = defineEmits<{ "update:open": [open: boolean] }>();

const columns = contactsTableColumns;
const contactsData = ref<SequenceAgent[]>([]);

const selectedRowKeys = ref<number[]>([]);

const onSelectChange = (changableRowKeys: number[]) => {
  selectedRowKeys.value = changableRowKeys;
};

const observerFlagRef = ref(null);
const currentTablePage = ref(1);
const nextPage = ref([]);

const { pause, resume } = useIntersectionObserver(observerFlagRef, ([{ isIntersecting }]) => {
  console.log({ isIntersecting });
  if (isIntersecting && nextPage.value) {
    currentTablePage.value++;
    console.log("fetching more data");
    fetchAgents();
    // fetchExpensesTableData({ resetFilters: false });
  }
});

const rowSelection: TableProps["rowSelection"] = {
  selectedRowKeys: computed(() => selectedRowKeys.value),
  onChange: onSelectChange,
  onSelect: (nativeEvent: any, selected: any) => {
    console.log(`selectedRows lll: ${nativeEvent}`, "selected: ", selected);
  },
  getCheckboxProps: (record: any) => ({
    disabled: !(record.state == ContactStatus.Pending || record.state == ContactStatus.InProgress),
  }),
};

const handleClose = () => emit("update:open", false);

const loading = ref(false);

const handleDelete = (agent?: SequenceAgent) => {
  Modal.confirm({
    icon: createVNode(QuestionCircleOutlined),
    centered: true,
    title: agent
      ? `¿Estás segur@ de eliminar al agente ${agent.full_name} de la secuencia?`
      : "¿Estás segur@ de eliminar estos agentes de la secuencia?",
    okText: "Eliminar",
    onOk: async () => {
      if (!agent?.id && selectedRowKeys.value.length == 0) return;

      try {
        if (agent) {
          await sequenceService.deleteAgent([{ id: agent.id, type: agent.type }]);
          message.success("Agente eliminado");
        } else {
          const selectedRows = contactsData.value
            .filter((el) => selectedRowKeys.value.includes(el.id))
            .map((el) => ({ id: el.id, type: el.type }));
          await sequenceService.deleteAgent(selectedRows);
          message.success("Agentes eliminados");
        }
        selectedRowKeys.value = [];

        await fetchAgentsAllPages();
        // await fetchAgents();
      } catch (error) {
        message.error(`No se puedo eliminar correctamente`);
        console.error(error);
      }
    },
  });
};

const fetchAgentsAllPages = async () => {
  loading.value = true;
  let contacts: any[] = [];
  for (let page = 1; page <= currentTablePage.value; page++) {
    const response: any = await sequenceService.getAgentsById(props.sequenceId, page);
    contacts = [...contacts, ...response?.results];
    nextPage.value = response?.next;
  }
  contactsData.value = contacts;
  loading.value = false;
};

const fetchAgents = async ({ page = currentTablePage.value } = {}) => {
  loading.value = true;
  const response: any = await sequenceService.getAgentsById(props.sequenceId, page);
  const data = response?.results?.filter((el: any) => el.status == 2 || el.status == 1);
  contactsData.value = [...contactsData.value, ...data];
  nextPage.value = response?.next;
  loading.value = false;
};


onMounted(async () => {
  await fetchAgents();
});
</script>

<style scoped lang="sass">
.modal
  &__body
    color: $gray-9
    height: 400px
    display: grid
    grid-template-columns: repeat(4, 1fr)
    grid-template-rows: repeat(3, 1fr)
    gap: 12px

  &__picker
    margin-bottom: 12px

  &__paragraph
    margin: 16px 0

.radial
  display: grid
  place-content: center

  .progress
    text-align: center

.card
  border: 1px solid $gray-5
  border-radius: 6px
  padding: 5px 10px

  &__title
    font-weight: 600
    margin-bottom: 12px

  &__icon
    font-size: 32px
    margin: 4px 0

    & img
      display: block
      margin: 6px 0
      height: 36px

    &--red
      color: $red-6

  &__value
    font-size: 30px
    line-height: 38px
    margin-bottom: -4px

  &__accordion
    padding: 0

  &--stat
    display: flex
    flex-direction: column
    align-items: center
    padding: 20px 0

  &:nth-child(1)
    grid-column: 1 / 3
    grid-row: 1 / 3

  &:nth-child(2)
    grid-column: 1 / 1
    grid-row: 3 / 4

  &:nth-child(3)
    grid-column: 2 / 2
    grid-row: 3 / 4

  &:nth-child(4)
    grid-column: 3 / 5
    grid-row: 1 / 4

.from-picker
  display: flex
  margin-top: 20px
  .ant-form-item
    width: 50%
    margin-right: 10px
  .ant-picker
    width: 100%

.counter
  color: $gray-8
  background-color: $geekblue-4
  padding: 4px 0
  text-align: center

:global(.sequence-contacts .ant-modal-footer)
  text-align: center
:global(.footer_btn.ant-btn)
  width: 300px

.contacts-list
  margin-top: 20px

.refresh-wrapper
  width: 100%
  text-align: right
</style>
